import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import HelmetWrapper from '../components/common/HelmetWrapper';
import PageLayout from '../components/layout/PageLayout';
// import AdSenseAd from '../components/ads/AdSenseAd'; // Removed to prevent AdSense violations
import SocietyInternalNav from '../components/societies/SocietyInternalNav';
import SocietyDetails from '../components/societies/sections/SocietyDetails';
import SocietyAnnouncements from '../components/societies/sections/SocietyAnnouncements';
import SocietyPosts from '../components/societies/sections/SocietyPosts';
import SocietyBusinesses from '../components/societies/sections/SocietyBusinesses';
import SocietyEvents from '../components/societies/sections/SocietyEvents';
import SocietyProperty from '../components/societies/sections/SocietyProperty';
import SocietyAds from '../components/societies/sections/SocietyAds';
import SocietyFreeItems from '../components/societies/sections/SocietyFreeItems';
import SocietyInstitutes from '../components/societies/sections/SocietyInstitutes';
import SocietyMosques from '../components/societies/sections/SocietyMosques';
import SocietyPlaces from '../components/societies/sections/SocietyPlaces';
import SocietyReviews from '../components/societies/SocietyReviews';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { societyService } from '../services/societyService';
import { getSlugFromParams } from '../utils/urlHelpers';
import { deriveSocietyHubSectionFromPathname } from '../utils/societyHubNavigation';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isSocietyPageIndexable } from '../utils/societySeo';

const SocietyDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(() =>
    params.tab || deriveSocietyHubSectionFromPathname(location.pathname),
  );
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  // Get the society identifier (slug or ID)
  const societyIdentifier = getSlugFromParams(params, 'society');
  
  // Load society data
  useEffect(() => {
    const loadSociety = async () => {
      try {
        setLoading(true);
        const societyData = await societyService.getSociety(societyIdentifier);
        setSociety(societyData);
        setIsFollowing(societyData.is_following || false);
        setFollowerCount(societyData.follower_count || 0);
      } catch (err) {
        console.error('Error loading society:', err);
        setError('Failed to load society details');
      } finally {
        setLoading(false);
      }
    };

    if (societyIdentifier) {
      loadSociety();
    }
  }, [societyIdentifier]);

  // Keep tab in sync when URL changes (deep links / router navigate from create flows).
  useLayoutEffect(() => {
    if (params.tab) {
      setActiveSection(params.tab);
      return;
    }
    setActiveSection(deriveSocietyHubSectionFromPathname(location.pathname));
  }, [location.pathname, params.tab]);

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!isAuthenticated) {
      showError('Please login to follow societies');
      return;
    }

    if (!society) return;

    try {
      setFollowLoading(true);
      const response = await societyService.toggleFollowSociety(society.id, isFollowing);
      setIsFollowing(response.is_following);
      setFollowerCount(response.follower_count);
      showSuccess(response.message);
    } catch (err) {
      console.error('Error toggling follow:', err);
      showError(err.message || 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  // Handle section navigation
  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Update URL without page reload
    const newUrl = section === 'details' 
      ? `/societies/${societyIdentifier}`
      : `/societies/${societyIdentifier}/${section}`;
    window.history.pushState({}, '', newUrl);
  };

  // Render active section
  const renderActiveSection = () => {
    if (!society) return null;

    const sectionProps = {
      society,
      onError: setError,
      onSocietyUpdated: setSociety,
    };

    switch (activeSection) {
      case 'details':
        return <SocietyDetails {...sectionProps} onNavigateToReviews={() => setActiveSection('reviews')} />;
      case 'announcements':
        return <SocietyAnnouncements {...sectionProps} />;
      case 'posts':
        return <SocietyPosts {...sectionProps} />;
      case 'businesses':
        return <SocietyBusinesses {...sectionProps} />;
      case 'events':
        return <SocietyEvents {...sectionProps} />;
      case 'property':
        return <SocietyProperty {...sectionProps} />;
      case 'ads':
        return <SocietyAds {...sectionProps} />;
      case 'free-items':
        return <SocietyFreeItems {...sectionProps} />;
      case 'institutes':
        return <SocietyInstitutes {...sectionProps} />;
      case 'mosques':
        return <SocietyMosques {...sectionProps} />;
      case 'places':
        return <SocietyPlaces {...sectionProps} />;
      case 'reviews':
        return <SocietyReviews 
          societyId={parseInt(society.id, 10)} 
          societyName={society.name} 
          onNavigateToReviews={() => setActiveSection('reviews')}
        />;
      default:
        return <SocietyDetails {...sectionProps} />;
    }
  };

  if (loading) {
    return (
      <PageLayout title="Loading Society">
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Error">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/societies')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Back to Societies
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!society) {
    return (
      <PageLayout title="Society Not Found">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Society Not Found</h1>
            <p className="text-gray-600 mb-4">The society you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/societies')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Back to Societies
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const societyIndexable = isSocietyPageIndexable(society.description);

  return (
    <PageLayout>
      <HelmetWrapper>
        <title>{society.name} - PakPoints</title>
        {!societyIndexable ? <meta name="robots" content="noindex,follow" /> : null}
        <meta name="description" content={society.description || `Discover ${society.name} society on PakPoints. ${society.city ? `Located in ${society.city}.` : ''} Find businesses, events, property listings and more.`} />
        <meta property="og:title" content={`${society.name} - PakPoints`} />
        <meta property="og:description" content={society.description || `Discover ${society.name} society on PakPoints.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.pakpoints.com/societies/${society.id}`} />
        {society.images && society.images.length > 0 && (
          <meta property="og:image" content={society.images[0].original_image} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${society.name} - PakPoints`} />
        <meta name="twitter:description" content={society.description || `Discover ${society.name} society on PakPoints.`} />
        {society.images && society.images.length > 0 && (
          <meta name="twitter:image" content={society.images[0].original_image} />
        )}
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "name": society.name,
            "description": society.description,
            "address": society.city ? {
              "@type": "PostalAddress",
              "addressLocality": society.city
            } : undefined,
            "aggregateRating": society.rating > 0 ? {
              "@type": "AggregateRating",
              "ratingValue": society.rating,
              "reviewCount": society.total_ratings
            } : undefined,
            "image": society.images && society.images.length > 0 ? society.images[0].original_image : undefined
          })}
        </script>
      </HelmetWrapper>

      <div className="bg-gray-50">
        {/* Mobile Society Navigation - Sticky below main navbar */}
        <div className="lg:hidden sticky top-16 z-40 bg-white border-b shadow-sm">
          <div className="px-4 py-3">
            <SocietyInternalNav
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              society={society}
            />
          </div>
        </div>

        {/* Top Ad - Removed to avoid header confusion */}

        <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            {/* Left Sidebar - Navigation (Desktop only) */}
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-6">
                <SocietyInternalNav
                  activeSection={activeSection}
                  onSectionChange={handleSectionChange}
                  society={society}
                />
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Society Header — compact on mobile, richer on desktop */}
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
                {/* Mobile: single compact row with name + city + icon follow */}
                <div className="sm:hidden flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <h1 className="text-base font-semibold text-gray-900 truncate">
                      {society.name}
                    </h1>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      {society.city && (
                        <span className="inline-flex items-center truncate">
                          <svg className="w-3.5 h-3.5 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="truncate">{society.city}</span>
                        </span>
                      )}
                      {society.rating > 0 && (
                        <span className="inline-flex items-center gap-0.5">
                          <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          {society.rating_display || society.rating}
                        </span>
                      )}
                      {followerCount > 0 && (
                        <span className="truncate">
                          {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    aria-label={isFollowing ? 'Unfollow society' : 'Follow society'}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium transition-colors ${
                      isFollowing
                        ? 'bg-white border border-green-500 text-green-600'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {followLoading ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill={isFollowing ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )}
                    <span>{isFollowing ? 'Following' : 'Follow'}</span>
                  </button>
                </div>

                {/* Desktop header (unchanged layout, kept for parity) */}
                <div className="hidden sm:block p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        {society.name}
                      </h1>
                      {society.city && (
                        <div className="flex items-center text-gray-600 mb-3">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-lg">{society.city}</span>
                        </div>
                      )}
                      {society.rating > 0 && (
                        <div className="flex items-center">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`w-5 h-5 ${i < Math.floor(society.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="ml-2 text-base text-gray-600">
                            {society.rating_display} ({society.total_ratings} reviews)
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mt-0">
                      <button
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                        className={`w-auto px-6 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center ${
                          isFollowing
                            ? 'bg-white border-2 border-green-500 text-green-600 hover:bg-green-50'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {followLoading ? (
                          <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className={`w-5 h-5 mr-2 ${isFollowing ? 'text-green-600' : 'text-white'}`} fill={isFollowing ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        )}
                        {isFollowing ? 'Following' : 'Follow Society'}
                      </button>
                      {followerCount > 0 && (
                        <p className="text-sm text-gray-600 mt-2 text-left">
                          {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Content */}
              <div
                className="mt-4 sm:mt-6"
                id={`society-panel-${activeSection}`}
                role="tabpanel"
                aria-labelledby={`society-tab-${activeSection}`}
              >
                {renderActiveSection()}
              </div>
            </div>

            {/* Right Sidebar - Removed ads to avoid navigation confusion */}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default SocietyDetailPage;
